import Close16 from '@carbon/icons-react/es/close/16';
import { DrawerProps } from 'antd';
import { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { BodyShort01, Text02 } from 'components/styles';
import {
  ProductiveHeading03,
  BodyShort01 as OzoneBodyShort01,
  TextWrapper,
} from 'ozone/atoms';
import { Drawer as DrawerAtom } from './drawer';

export const CustomLayoutDrawer = styled(DrawerAtom)`
  .ant-drawer-header {
    padding: 0;
  }

  .ant-drawer-close {
    display: flex;
    position: absolute;
    top: 0;
    right: 0;
  }
  .ant-drawer-body {
    padding: 0;
  }
`;

const Divider = styled.div`
  border-top: 1px solid var(--border-color);
`;

const Separator = styled.div`
  margin: 0 var(--spacing-02);

  &::after {
    content: '·';
  }
`;

const Text = styled(TextWrapper)`
  padding: 0;
`;
const TitleDescription = styled.div`
  ${BodyShort01};
  color: var(--gray-50);
`;
const Title = styled(ProductiveHeading03).attrs({ as: Text })``;

interface DrawerTitleProps {
  title?: ReactNode;
  description?: ReactNode;
  className?: string;
}
const TitleWithDescription: FC<React.PropsWithChildren<DrawerTitleProps>> = ({
  title,
  description,
  children,
  className,
  ...props
}) => {
  return (
    <Title className={className} {...props}>
      {children != null ? (
        children
      ) : (
        <>
          {title}
          {description != null ? (
            <TitleDescription>{description}</TitleDescription>
          ) : null}
        </>
      )}
    </Title>
  );
};
const StyledTitle = styled(TitleWithDescription)``;

type DrawerTitle = typeof StyledTitle & {
  Title: typeof Title;
  Description: typeof TitleDescription;
};
const DrawerTitle: DrawerTitle = StyledTitle as any;

DrawerTitle.Title = Title;
DrawerTitle.Description = TitleDescription;

const Content = styled(OzoneBodyShort01)``;

const Footer = styled.div``;

const DrawerComponent = styled.div`
  --popover-spacing: var(--spacing-05);
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  height: 100%;

  ${Title} {
    padding: var(--popover-spacing) var(--popover-spacing)
      var(--popover-spacing);
    background: var(--coolest-gray-10);
    flex: 0 0 auto;
  }

  ${Content} {
    padding: var(--popover-spacing) var(--popover-spacing)
      calc(2 * var(--popover-spacing));
    flex: 1;
    overflow-y: auto;
  }

  ${Footer} {
    border-top: 1px solid var(--border-color);
    flex: 0 0 auto;
    padding: var(--popover-spacing);
  }

  ${Divider} {
    margin-top: var(--popover-spacing);
  }
`;

const HelpText = styled.div`
  ${Text02};
  padding: 8px 10px 10px 0;
  font-size: 14px;
  line-height: 1; /* Matches the font size exactly */

  a {
    margin-left: var(--spacing-02);
    font-size: 14px;
    padding: 8px 10px 10px 0;
    line-height: 1; /* Matches the font size exactly */
  }
`;

type CustomLayoutDrawerContent = typeof DrawerComponent & {
  Title: DrawerTitle;
  Content: typeof Content;
  Divider: typeof Divider;
  Separator: typeof Separator;
  Footer: typeof Footer;
  TitleHelpText: typeof HelpText;
};

const CustomLayoutDrawerContent: CustomLayoutDrawerContent =
  DrawerComponent as any;

CustomLayoutDrawerContent.Title = DrawerTitle as any;
CustomLayoutDrawerContent.Content = Content;
CustomLayoutDrawerContent.Divider = Divider;
CustomLayoutDrawerContent.Separator = Separator;
CustomLayoutDrawerContent.Footer = Footer;
CustomLayoutDrawerContent.TitleHelpText = HelpText;

export { CustomLayoutDrawerContent };
export const DefaultCustomLayoutDrawerProps: DrawerProps = {
  placement: 'right',
  closable: true,
  closeIcon: <Close16 data-cy="aifills.template.close.icon" />,
  mask: true,
  destroyOnClose: true,
};
